import { Stack, Typography } from '@mui/material';
import React from 'react';
import {
    DashboardActivityChart,
    DashboardKpiCard,
    DashboardKpiRow,
    useAllowedPath
} from '../../sections/@dashboard/app';
import { formatCurrency } from '../../utils/formatNumber';
import i18n from '../../i18n';

/**
 * The admin dashboard: what /api/users/dashboard-info returns for an admin
 * (AdminDashboard in x-backend) — four balance figures from
 * public.aggregate_admin_data and the daily payin/payout count from
 * dashboard_chart_data. The allpaypays widgets this replaced (debt to merchants
 * and providers, our commissions, orders by status) read fields no endpoint
 * sends, and showed zeros.
 *
 * The figures add terminal-currency totals across every terminal and currency
 * (docs/currencies-amounts-balances.md, "Admin dashboard tiles"), so they mean
 * something only while everything is in one currency — which the row says.
 */
const TILES = [
    { key: 'available', icon: 'eva:credit-card-outline', color: 'primary' },
    { key: 'received', icon: 'eva:diagonal-arrow-left-down-outline', color: 'success', path: '/app/payin-instances' },
    { key: 'pending', icon: 'eva:clock-outline', color: 'warning', path: '/app/payin-instances' },
    { key: 'total', icon: 'eva:layers-outline', color: 'info' }
];

export default function AdminDashboardContent({ data, isLoading }) {
    const allowed = useAllowedPath();
    const balance = data?.balance || {};
    const transactionsByDate = data?.transactionsByDate || {};
    const labels = data?.datesLabelsPayments || [];

    const t = (key) => i18n.t(`dashboard.admin.${key}`);

    return (
        <Stack spacing={3}>
            <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                    {t('allCurrencies')}
                </Typography>
                <DashboardKpiRow>
                    {TILES.map((tile) => (
                        <DashboardKpiCard
                            key={tile.key}
                            loading={isLoading}
                            title={t(`${tile.key}.title`)}
                            tooltip={t(`${tile.key}.tooltip`)}
                            value={formatCurrency(balance[`${tile.key}Balance`] || 0)}
                            icon={tile.icon}
                            color={tile.color}
                            to={tile.path && allowed(tile.path)}
                        />
                    ))}
                </DashboardKpiRow>
            </Stack>
            <DashboardActivityChart
                loading={isLoading}
                title={t('byDate')}
                labels={labels}
                series={Object.entries(transactionsByDate).map(([name, series]) => ({
                    name: i18n.t(`dashboard.${name}`),
                    data: series
                }))}
            />
        </Stack>
    );
}
